<script setup lang="ts">
import { onMounted, ref } from 'vue';

import type { Organization } from '@/types/organization';

import CustomButton from '@/components/CustomButton.vue';
import List from '@/components/List.vue';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';
import { useOrganizationStore } from '@/stores/organization';
import { useRoute } from 'vue-router';

const route = useRoute();
const organizationIdFromRoute = route.params.organizationId as string;

const organizationStore = useOrganizationStore();
const globalStore = useGlobalStore();
const authStore = useAuthStore();

const organizations = ref<Organization[]>([]);
const isInitializing = ref(true);

onMounted(async () => {
    if (organizationIdFromRoute) await organizationStore.fetchById(organizationIdFromRoute);
    organizations.value = await organizationStore.getDirectOrganizationChildren(organizationIdFromRoute);
    isInitializing.value = false;
});

const tableHeaders = [{ title: 'Naam', value: 'name', sortable: true }];

async function clickDeleteOrganization(item: Organization) {
    await organizationStore.deleteOrganization(item._id);
    organizations.value = organizations.value.filter((org) => org._id !== item._id);
}

async function clickEditOrganization(item: Organization) {
    await router.push(`/bedrijf-bewerken/${item?._id}`);
}

async function clickCreate() {
    await router.push(`/bedrijf-maken/${organizationIdFromRoute}`);
}
</script>

<template>
    <v-fade-transition>
        <div v-show="!isInitializing && !globalStore.isLoading">
            <div v-if="organizations.length == 0">
                <p>Dit bedrijf heeft geen vestigingen.</p>
                <CustomButton @click="clickCreate" text="Vestiging aanmaken" v-if="!authStore.isReadOnly()" />
            </div>
            <List
                v-else
                :title="`Vestigingen van ${organizationStore.organization.name}`"
                :headers="tableHeaders"
                :items="organizations"
                :createButtonCallback="clickCreate"
                entityName="Vestiging"
                :isEditable="true"
                :isDeletable="true"
                @clickTableRowEdit="clickEditOrganization"
                @deleteConfirmed="clickDeleteOrganization"
            >
                <template v-slot:item.name="{ item }">
                    <router-link
                        :to="`/bedrijf/${(item as any)._id}/informatie`"
                        class="d-flex align-center ga-1 text-black text-decoration-none pa-1 rounded-lg"
                    >
                        <v-icon icon="business" class="text-main" />
                        <span class="text-main font-weight-medium">{{ (item as any).name }}</span>
                    </router-link>
                </template>
            </List>
        </div>
    </v-fade-transition>
</template>

<style scoped></style>
